/**
 * Import Monitor — Rules & mappings tab.
 *
 * The quick-debug view of *how this tenant's feed is interpreted*: the
 * business rules the strategy bundle and handler apply, the feed-key alias
 * table each import service accepts, the profile toggles and performance
 * settings that change outcomes, and the raw keys the latest pushes
 * actually carried — with anything nobody maps called out.
 * Everything is self-described by the backend code that applies it, so it
 * renders for every tenant without any middleware involvement.
 *
 * Platform superusers only — the parent hides the tab and the endpoint
 * rejects everyone else.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, BookOpen, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { importRulesAPI, type ImportRulesCatalogue, type ImportRuleItem } from '../../lib/api';

interface ImportRulesTabProps {
  customerId: number;
}

const SOURCE_LABEL: Record<string, string> = {
  closing: 'Open / close',
  grn_linking: 'GRN linking',
  mapping: 'Field mapping',
  handler: 'Handler (pre/post)',
};

function fmt(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function ImportRulesTab({ customerId }: ImportRulesTabProps) {
  const [data, setData] = useState<ImportRulesCatalogue | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await importRulesAPI.getRules(customerId));
    } catch (err: unknown) {
      console.error('Failed to load import rules:', err);
      setError(err instanceof Error ? err.message : 'Failed to load import rules');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { void load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading rules…
      </div>
    );
  }
  if (error) {
    return (
      <Card><CardContent className="py-8 text-center text-destructive">
        {error}
        <div className="mt-3"><Button variant="outline" size="sm" onClick={() => void load()}>Retry</Button></div>
      </CardContent></Card>
    );
  }
  if (!data) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground">Select a customer to see its import rules.</CardContent></Card>;
  }

  const grouped = data.rules.reduce<Record<string, ImportRuleItem[]>>((acc, r) => {
    (acc[r.source] ??= []).push(r);
    return acc;
  }, {});
  const unmappedTotal = data.observed.reduce((n, o) => n + o.unmapped_keys.length, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4" /> {data.customer.name} — import rules
              </CardTitle>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">handler: {data.handler.name}</Badge>
                <Badge variant="outline">closing: {data.handler.strategies.closing}</Badge>
                <Badge variant="outline">grn: {data.handler.strategies.grn_linking}</Badge>
                <Badge variant="outline">mapping: {data.handler.strategies.mapping}</Badge>
                {data.customer.base_currency && <Badge variant="outline">reporting ccy: {data.customer.base_currency}</Badge>}
                {unmappedTotal > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" /> {unmappedTotal} unmapped feed key{unmappedTotal === 1 ? '' : 's'}
                  </Badge>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="rules" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="rules">Business rules ({data.rules.length})</TabsTrigger>
          <TabsTrigger value="observed">Latest feed ({data.observed.length})</TabsTrigger>
          <TabsTrigger value="services">Field aliases ({data.services.length})</TabsTrigger>
          <TabsTrigger value="settings">Profile &amp; settings</TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          <Card><CardContent className="pt-4">
            {Object.entries(grouped).map(([source, items]) => (
              <div key={source} className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {SOURCE_LABEL[source] ?? source}
                </div>
                <Table>
                  <TableBody>
                    {items.map((r, i) => (
                      <TableRow key={`${source}-${i}`}>
                        <TableCell className="w-40 align-top text-xs font-medium">{r.area}</TableCell>
                        <TableCell className="align-top text-sm">
                          {r.rule}
                          {r.detail && <div className="text-xs text-muted-foreground mt-0.5">{r.detail}</div>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="observed">
          <Card><CardContent className="pt-4">
            {data.observed.length === 0 && <div className="text-sm text-muted-foreground py-2">No import jobs yet for this customer.</div>}
            {data.observed.map(o => (
              <div key={o.data_type} className="mb-4 rounded border p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{o.data_type}</span>
                  <Badge variant="outline">job #{o.job_id}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(o.at).toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">
                    {o.total_records} rows · {o.records_created} created · {o.records_updated} updated · {o.records_failed} failed
                  </span>
                </div>
                <div className="mt-2 grid gap-2 md:grid-cols-2 text-xs">
                  <div>
                    <div className="font-semibold mb-1">Raw keys ({o.raw_keys.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {o.raw_keys.map(k => (
                        <Badge key={k} variant={o.unmapped_keys.includes(k) ? 'destructive' : 'secondary'} className="font-mono text-[11px]">{k}</Badge>
                      ))}
                      {o.raw_keys.length === 0 && <span className="text-muted-foreground">no sample captured</span>}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">After pre-processing ({o.processed_keys.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {o.processed_keys.map(k => <Badge key={k} variant="outline" className="font-mono text-[11px]">{k}</Badge>)}
                    </div>
                  </div>
                </div>
                {o.envelope_keys.length > 0 && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Pipeline envelope: {o.envelope_keys.join(', ')}
                  </div>
                )}
                {o.unmapped_keys.length > 0 && (
                  <div className="mt-2 text-xs text-destructive">
                    Unmapped (no service reads these): {o.unmapped_keys.join(', ')}
                  </div>
                )}
                {o.sample_raw[0] && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Sample record (raw → processed)</summary>
                    <div className="grid gap-2 md:grid-cols-2 mt-1">
                      <pre className="overflow-x-auto rounded bg-muted p-2">{JSON.stringify(o.sample_raw[0], null, 2)}</pre>
                      <pre className="overflow-x-auto rounded bg-muted p-2">{JSON.stringify(o.sample_processed[0] ?? {}, null, 2)}</pre>
                    </div>
                  </details>
                )}
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="services">
          <Card><CardContent className="pt-4">
            <div className="grid gap-3 md:grid-cols-2">
              {data.services.map(s => (
                <div key={s.data_type} className="rounded border p-3">
                  <div className="text-sm font-medium">{s.data_type}</div>
                  <div className="text-xs text-muted-foreground font-mono mb-2">{s.service}</div>
                  {s.aliases.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Canonical keys only (no aliases)</div>
                  ) : (
                    <Table>
                      <TableHeader><TableRow><TableHead className="h-7 text-xs">Feed key</TableHead><TableHead className="h-7 text-xs">Canonical</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {s.aliases.map((a, i) => (
                          <TableRow key={`${s.data_type}-${i}`}>
                            <TableCell className="py-1 font-mono text-xs">{a.source}</TableCell>
                            <TableCell className="py-1 font-mono text-xs">{a.canonical}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card><CardContent className="pt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Import profile</div>
                {Object.keys(data.profile).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No import profile — default handler, default toggles.</div>
                ) : (
                  <Table><TableBody>
                    {Object.entries(data.profile).map(([k, v]) => (
                      <TableRow key={k}><TableCell className="py-1 font-mono text-xs">{k}</TableCell><TableCell className="py-1 text-xs">{fmt(v)}</TableCell></TableRow>
                    ))}
                  </TableBody></Table>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Delivery performance settings</div>
                <Table><TableBody>
                  {Object.entries(data.settings).map(([k, v]) => (
                    <TableRow key={k}><TableCell className="py-1 font-mono text-xs">{k}</TableCell><TableCell className="py-1 text-xs">{fmt(v)}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
